/*
 * ss-slider-loop.js
 * ------------------------------------------------------------------
 * Section-scoped seamless-loop slider for the "Parallelogram Collection"
 * section. Subclasses Dawn's native SliderComponent (assets/global.js) so it
 * reuses the native CSS scroll-snap engine, button binding (name="previous" /
 * name="next"), and page tracking — with NO Swiper dependency.
 *
 * SliderComponent alone is a finite slider (it disables the arrows at the ends).
 * This subclass adds a clone-based infinite loop:
 *   - The Liquid renders the slide set THREE times (identical clones).
 *   - We start scrolled into the MIDDLE set.
 *   - Once a manual scroll has settled (finger lifted, momentum finished) we
 *     reset scrollLeft by one "set width" if it has drifted more than half a set
 *     from home, so the wrap is invisible (the clones are identical). Never
 *     during a touch: moving scrollLeft under a finger made swipes glitch,
 *     worst when swiping left, which used to cross the seam straight away.
 *   - Arrow clicks normalise into the middle band first, then animate one slide.
 *
 * Tradeoff: because this rides CSS scroll-snap (`scroll-snap-type: x mandatory`),
 * a very fast fling that crosses the seam mid-momentum can show a tiny jump on
 * some browsers. That is the cost of dropping the Swiper library. There is no
 * autoplay, so the ends are otherwise unnoticeable.
 *
 * We do NOT modify the shared SliderComponent — this element registers under its
 * own tag <ss-slider-loop> and is used only by this section.
 */
(function () {
  function defineLoop() {
    if (typeof SliderComponent === 'undefined') return false;
    if (customElements.get('ss-slider-loop')) return true;

    class SSSliderLoop extends SliderComponent {
      constructor() {
        super();

        // Base constructor bails (returns early) when the track or next button
        // is missing; mirror that so we never touch undefined refs.
        if (!this.slider || !this.nextButton) return;

        // Stop the base class from disabling the arrows at the ends — in loop
        // mode the arrows are always active (parity with Swiper loop:true).
        this.enableSliderLooping = true;

        this.realCount = parseInt(this.dataset.slidesCount, 10) || 0;
        this.setCount = parseInt(this.dataset.sets, 10) || 3;
        this.isAnimating = false;
        this._loopReady = false;
        this._rafPending = false;

        this.prevButton.removeAttribute('disabled');
        this.nextButton.removeAttribute('disabled');

        // Seam correction waits until scrolling has settled and no finger is
        // down: 'scrollend' where supported, otherwise 150ms after the last
        // scroll event.
        this._touching = false;
        this.slider.addEventListener('touchstart', () => { this._touching = true; }, { passive: true });
        const release = () => { this._touching = false; this.settleSoon(); };
        this.slider.addEventListener('touchend', release, { passive: true });
        this.slider.addEventListener('touchcancel', release, { passive: true });
        this.slider.addEventListener('scroll', () => this.settleSoon(), { passive: true });
        this.slider.addEventListener('scrollend', () => {
          clearTimeout(this._settleTimer);
          if (!this._touching && !this.isAnimating) this.normalize();
        });

        this.positionToHome();
      }

      // Width of one clone set (real slides + their gaps). The slide pitch
      // (`sliderItemOffset`) is measured by the base class from the DOM, so this
      // stays correct across breakpoints and gap changes.
      get setWidth() {
        return this.realCount * (this.sliderItemOffset || 0);
      }

      // Base measures pitch / pages here and is also called on every resize via
      // its ResizeObserver. We re-home only until the first successful measure.
      initPages() {
        super.initPages();
        if (!this._loopReady) this.positionToHome();
      }

      positionToHome() {
        const w = this.setWidth;
        if (!w || this.realCount < 1) return;
        this.setScrollInstant(w); // start of the middle set
        this._loopReady = true;
      }

      // Instant (non-animated) scroll, defeating the CSS `scroll-behavior: smooth`
      // so the seam reset never animates.
      setScrollInstant(left) {
        const prev = this.slider.style.scrollBehavior;
        this.slider.style.scrollBehavior = 'auto';
        this.slider.scrollLeft = left;
        void this.slider.offsetWidth; // lock in the jump before restoring
        this.slider.style.scrollBehavior = prev || '';
      }

      settleSoon() {
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(() => {
          if (!this._touching && !this.isAnimating) this.normalize();
        }, 150);
      }

      // Keep the scroll position within half a set of home [0.5w, 1.5w].
      // Jumping by exactly one set lands on an identical clone → invisible.
      normalize() {
        const w = this.setWidth;
        if (!w) return;
        const x = this.slider.scrollLeft;
        if (x < w / 2) {
          this.setScrollInstant(x + w);
        } else if (x > w * 1.5) {
          this.setScrollInstant(x - w);
        }
      }

      // Override the base arrow handler: normalise into the home band first so
      // the smooth one-slide animation target never lands outside the clones,
      // then correct the seam once the animation settles.
      onButtonClick(event) {
        event.preventDefault();
        const w = this.setWidth;
        if (!w) {
          super.onButtonClick(event);
          return;
        }

        this.normalize();

        const step = parseFloat(event.currentTarget.dataset.step) || 1;
        const dir = event.currentTarget.name === 'next' ? 1 : -1;
        const target = this.slider.scrollLeft + dir * step * this.sliderItemOffset;

        this.isAnimating = true;
        clearTimeout(this._animTimer);
        this.slider.scrollTo({ left: target, behavior: 'smooth' });
        this._animTimer = setTimeout(() => {
          this.isAnimating = false;
          this.normalize();
        }, 450);
      }
    }

    customElements.define('ss-slider-loop', SSSliderLoop);
    return true;
  }

  // `defer` guarantees global.js (which defines SliderComponent) runs first, but
  // guard anyway in case of unusual load ordering.
  if (!defineLoop()) {
    document.addEventListener('DOMContentLoaded', defineLoop);
  }
})();
